import { ChevronRight } from "@mui/icons-material";
import { List, ListItemButton, ListSubheader, Typography } from "@mui/material";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, To } from "react-router";

import { ProjectReviewVisitV2FunctionListItem } from "../ProjectReviewVisitV2FunctionListItem";
import { ProjectReviewVisitV2FunctionListItemFragment } from "../ProjectReviewVisitV2FunctionListItem/fragments.generated";

type Props = {
    createTo: (ecologicalFunction: ProjectReviewVisitV2FunctionListItemFragment) => To;
    ecologicalFunctions: ProjectReviewVisitV2FunctionListItemFragment[];
};

type EcologicalFunctionGroup = {
    id: string;
    name: string;
    ecologicalFunctions: ProjectReviewVisitV2FunctionListItemFragment[];
};

function groupObservationsByType(
    ecologicalFunctions: ProjectReviewVisitV2FunctionListItemFragment[],
): EcologicalFunctionGroup[] {
    const groups = new Map<string, EcologicalFunctionGroup>();

    for (const fn of ecologicalFunctions) {
        const typeId = fn.type.id;
        const group = groups.get(typeId);

        if (group) {
            group.ecologicalFunctions.push(fn);
        } else {
            groups.set(typeId, {
                id: typeId,
                name: fn.type.name,
                ecologicalFunctions: [fn],
            });
        }
    }

    return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

export function ProjectReviewVisitV2FunctionList({ createTo, ecologicalFunctions }: Props) {
    const { t } = useTranslation("project");
    const grouped = useMemo(() => groupObservationsByType(ecologicalFunctions), [ecologicalFunctions]);

    return (
        <List sx={{ padding: 0 }}>
            {ecologicalFunctions.length < 1 && (
                <Typography padding={2} color="textSecondary">
                    {t("projectReviewVisitV2.functionsRoute.noFunctions")}
                </Typography>
            )}
            {grouped.map(({ id: groupId, name, ecologicalFunctions: groupEcologicalFunctions }) => (
                <Fragment key={groupId}>
                    <ListSubheader
                        sx={theme => ({
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.default,
                        })}
                    >
                        <Typography flex={1} variant="overline" fontWeight="bold">
                            {name}
                        </Typography>
                    </ListSubheader>
                    {groupEcologicalFunctions.map(ecologicalFunction => (
                        <ListItemButton key={ecologicalFunction.id} component={Link} to={createTo(ecologicalFunction)}>
                            <ProjectReviewVisitV2FunctionListItem ecologicalFunction={ecologicalFunction} />
                            <ChevronRight />
                        </ListItemButton>
                    ))}
                </Fragment>
            ))}
        </List>
    );
}
